import { Avatar } from "@/components/ui/avatar"

import { ALL_CONTRIBUTORS } from "../_fixtures"

// Crop marks frame the content column: pre-press materiality, tonal ink.
// The page opens as a galley proof on the desk.
function CropMarks() {
  return (
    <div
      aria-hidden="true"
      className="text-batyr-ink/20 pointer-events-none absolute inset-3 lg:inset-6"
    >
      <span className="absolute top-0 left-0 size-4 border-t border-l border-current" />
      <span className="absolute top-0 right-0 size-4 border-t border-r border-current" />
      <span className="absolute bottom-0 left-0 size-4 border-b border-l border-current" />
      <span className="absolute right-0 bottom-0 size-4 border-r border-b border-current" />
    </div>
  )
}

// The masthead is itself a specimen: Batyr Display h1, an edition line,
// and a working byline strip. The page opens by practicing the patterns
// it documents. The single highlighter sweep here is the masthead's one
// yellow moment; crop marks + a slug line set the proof sheet.
export function Masthead() {
  const visible = ALL_CONTRIBUTORS.slice(0, 5)
  const overflow = ALL_CONTRIBUTORS.length - visible.length

  return (
    <header className="border-secondary relative border-b py-16 lg:py-24">
      <CropMarks />
      <div className="max-w-container relative mx-auto flex flex-col gap-8 px-4 lg:px-8">
        <div className="flex flex-col gap-5">
          <p className="text-quaternary text-xs font-semibold tracking-[0.12em] uppercase">
            Lists · Galley proof · Edition No. 1 · June 2026
          </p>
          <h1 className="text-display-md lg:text-display-lg text-primary text-balance">
            The Lists Desk
          </h1>
          <p className="text-secondary max-w-prose text-lg text-pretty">
            A newsroom table where peers assemble and pass around story
            mixtapes. Every component on this page is stock Untitled&nbsp;UI,
            toned by the Batyr palette and wrapped in the ui/ layer. What makes
            it Lists is care in composition. Here,{" "}
            <span className="highlighter">
              the &ldquo;why&rdquo; is the product
            </span>
            , and the people behind it are part of the design.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center -space-x-2">
            {visible.map((contributor) => (
              <Avatar
                key={contributor.id}
                name={contributor.display_name}
                avatarUrl={contributor.user?.avatar_url}
                size="sm"
                className="ring-2 ring-white"
              />
            ))}
          </div>
          <p className="text-tertiary text-sm">
            Maintained by many hands: Priya, June, Theo
            {overflow > 0 && ` + ${overflow} others`}
          </p>
        </div>

        <div className="grid max-w-4xl gap-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-primary text-md font-semibold">What this is</h2>
            <p className="text-secondary max-w-prose text-sm">
              Warm print materiality: a cream canvas, ink type, paper-white
              proofs. Yellow works like a highlighter on the one thing that
              matters. Bylines, contributors, and &ldquo;why it matters&rdquo;
              notes are first-class design elements. We spend energy in a few
              deliberate places and leave the rest quiet.
            </p>
          </div>
          <div className="flex flex-col gap-1.5">
            <h2 className="text-primary text-md font-semibold">
              What this is not
            </h2>
            <p className="text-secondary max-w-prose text-sm">
              Not a clinical health portal, a Spotify clone, a SaaS dashboard,
              or a social feed. And none of the nearer misses: whiteboard
              cosplay, grayscale minimalism, gamified peer mechanics, LMS
              completion energy, single-author solemnity, podcast-app chrome.
            </p>
          </div>
        </div>
      </div>
    </header>
  )
}
